/**
 * PaymentAuditTrail Component
 * Collapsible "Payment History" pane — fetches from the project history
 * endpoint and filters client-side to payment-related changeTypes.
 */

import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';

const PAYMENT_CHANGE_TYPES = new Set([
  'payment_milestone_created',
  'payment_milestone_updated',
  'payment_milestone_deleted',
  'payment_recorded',
  'payment_updated',
  'payment_deleted',
  'vo_created',
  'vo_updated',
  'vo_deleted',
  'vo_voided',
  'vo_payment_recorded',
  'vo_payment_updated',
  'vo_payment_deleted',
  'deduction_added',
  'deduction_updated',
  'deduction_deleted',
  'payment_notice_issued',
  'payment_notice_cancelled',
  'payment_notice_fulfilled'
]);

const CHANGE_TYPE_LABELS = {
  payment_milestone_created: 'Milestone created',
  payment_milestone_updated: 'Milestone updated',
  payment_milestone_deleted: 'Milestone deleted',
  payment_recorded: 'Payment recorded',
  payment_updated: 'Payment updated',
  payment_deleted: 'Payment deleted',
  vo_created: 'VO created',
  vo_updated: 'VO updated',
  vo_deleted: 'VO deleted',
  vo_voided: 'VO voided',
  vo_payment_recorded: 'VO payment recorded',
  vo_payment_updated: 'VO payment updated',
  vo_payment_deleted: 'VO payment deleted',
  deduction_added: 'Deduction added',
  deduction_updated: 'Deduction updated',
  deduction_deleted: 'Deduction deleted',
  payment_notice_issued: 'Notice issued',
  payment_notice_cancelled: 'Notice cancelled',
  payment_notice_fulfilled: 'Notice fulfilled'
};

const CHANGE_TYPE_COLORS = {
  payment_recorded: 'bg-green-100 text-green-700',
  vo_payment_recorded: 'bg-green-100 text-green-700',
  payment_updated: 'bg-blue-100 text-blue-700',
  vo_payment_updated: 'bg-blue-100 text-blue-700',
  payment_milestone_updated: 'bg-blue-100 text-blue-700',
  vo_updated: 'bg-blue-100 text-blue-700',
  payment_milestone_created: 'bg-indigo-100 text-indigo-700',
  vo_created: 'bg-indigo-100 text-indigo-700',
  payment_deleted: 'bg-red-100 text-red-700',
  vo_payment_deleted: 'bg-red-100 text-red-700',
  payment_milestone_deleted: 'bg-red-100 text-red-700',
  vo_deleted: 'bg-red-100 text-red-700',
  vo_voided: 'bg-red-100 text-red-700',
  deduction_added: 'bg-orange-100 text-orange-700',
  deduction_updated: 'bg-orange-100 text-orange-700',
  deduction_deleted: 'bg-orange-100 text-orange-700',
  payment_notice_issued: 'bg-purple-100 text-purple-700',
  payment_notice_cancelled: 'bg-gray-100 text-gray-700',
  payment_notice_fulfilled: 'bg-green-100 text-green-700'
};

function formatDateTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function parseJSON(val) {
  if (!val) return null;
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val);
  } catch {
    return val;
  }
}

function summarizeValue(val) {
  if (val == null) return null;
  const parsed = parseJSON(val);
  if (parsed == null) return null;
  if (typeof parsed !== 'object') return String(parsed);
  const parts = [];
  for (const [k, v] of Object.entries(parsed)) {
    if (v == null) continue;
    if (typeof v === 'object') continue;
    parts.push(`${k}: ${v}`);
    if (parts.length >= 4) break;
  }
  return parts.length ? parts.join(' · ') : null;
}

const PaymentAuditTrail = ({ projectId }) => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (open && !loaded && projectId) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/design-projects/${projectId}/history`);
      setEntries(res.data.history || []);
      setLoaded(true);
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const paymentEntries = useMemo(() => {
    return entries.filter(e => PAYMENT_CHANGE_TYPES.has(e.changeType));
  }, [entries]);

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between text-left hover:bg-gray-50 -mx-2 px-2 py-2 rounded-lg min-h-[44px] touch-manipulation"
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h4 className="text-sm font-semibold text-gray-900">Payment History</h4>
          {loaded && paymentEntries.length > 0 && (
            <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full">
              {paymentEntries.length}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="mt-3">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
            </div>
          )}

          {error && !loading && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
              <button
                onClick={fetchHistory}
                className="ml-2 underline hover:text-red-900"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && paymentEntries.length === 0 && (
            <div className="text-center py-6 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">No payment activity recorded yet</p>
            </div>
          )}

          {!loading && !error && paymentEntries.length > 0 && (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {paymentEntries.map(entry => {
                const label = CHANGE_TYPE_LABELS[entry.changeType] || entry.changeType;
                const badge = CHANGE_TYPE_COLORS[entry.changeType] || 'bg-gray-100 text-gray-700';
                const oldSummary = summarizeValue(entry.oldValue);
                const newSummary = summarizeValue(entry.newValue);
                const actor = entry.user?.nickname || entry.user?.fullName || 'System';
                return (
                  <div
                    key={entry.id}
                    className="border border-gray-200 rounded-lg p-3 bg-white"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge}`}>
                            {label}
                          </span>
                          <span className="text-xs text-gray-500">{formatDateTime(entry.timestamp)}</span>
                        </div>
                        {entry.description && (
                          <p className="text-sm text-gray-700 mt-1">{entry.description}</p>
                        )}
                        {(oldSummary || newSummary) && (
                          <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                            {oldSummary && (
                              <p>
                                <span className="text-gray-400">before:</span> {oldSummary}
                              </p>
                            )}
                            {newSummary && (
                              <p>
                                <span className="text-gray-400">after:</span> {newSummary}
                              </p>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-1">by {actor}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentAuditTrail;
